'use client'

import { useEffect, useRef, useState } from 'react'
import { useCatchScroll } from '@/utils/useCatchScroll'
import { Icon } from '../Icon'
import { Typography } from '../Typography'
import styles from './ScrollToTop.module.scss'

const SCROLL_SHOW_OFFSET = 500
const AUTO_HIDE_DELAY = 5000

const ScrollToTop = () => {
  const [showGoTop, setShowGoTop] = useState(false)
  const previousScrollYRef = useRef(0)
  const hideTimeoutRef = useRef<ReturnType<typeof globalThis.setTimeout> | undefined>(undefined)

  const { scrollDir } = useCatchScroll()

  const restartHideTimer = () => {
    if (hideTimeoutRef.current !== undefined) {
      globalThis.clearTimeout(hideTimeoutRef.current)
    }

    hideTimeoutRef.current = globalThis.setTimeout(() => {
      setShowGoTop(false)
    }, AUTO_HIDE_DELAY)
  }

  // Show the button when scrolling up after offset and hide it when scrolling down.
  // Once visible, hide it again after AUTO_HIDE_DELAY without any new scroll event.
  useEffect(() => {
    previousScrollYRef.current = globalThis.scrollY ?? 0

    const handleScroll = () => {
      const scrollY = globalThis.scrollY ?? 0

      if (scrollY < SCROLL_SHOW_OFFSET) {
        setShowGoTop(false)
        if (hideTimeoutRef.current !== undefined) {
          globalThis.clearTimeout(hideTimeoutRef.current)
        }
      } else if (scrollY < previousScrollYRef.current) {
        if (!showGoTop) {
          setShowGoTop(true)
        }
        restartHideTimer()
      } else if (scrollY > previousScrollYRef.current) {
        setShowGoTop(false)
      } else {
        restartHideTimer()
      }

      previousScrollYRef.current = scrollY
    }

    globalThis.addEventListener('scroll', handleScroll, {
      passive: true,
    })

    return () => {
      if (hideTimeoutRef.current !== undefined) {
        globalThis.clearTimeout(hideTimeoutRef.current)
      }
      globalThis.removeEventListener('scroll', handleScroll)
    }
  }, [showGoTop])

  const handleScrollUp = () => {
    globalThis.scrollTo({ top: 0, behavior: 'smooth' })
    setShowGoTop(false)
  }

  return (
    <button
      type="button"
      data-cy="scroll-to-top"
      className={showGoTop && scrollDir === 'scrolling up' ? styles['scroll-top'] : styles.hidden}
      onClick={handleScrollUp}
    >
      <Icon name="arrowUp" />
      <Typography as="span" variant="s">
        nach oben
      </Typography>
    </button>
  )
}

export default ScrollToTop
